import { useState } from "react";

// ============================================
// ICONS
// ============================================

function IconCalendar({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5"
      />
    </svg>
  );
}

function IconPhone({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z"
      />
    </svg>
  );
}

function IconClock({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function IconChevronDown({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
      />
    </svg>
  );
}

function IconX({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  );
}

// ============================================
// TYPES
// ============================================

export interface SchedulingData {
  scheduledCallAt: string | null; // ISO datetime
  appealDeadline: string | null; // ISO date (YYYY-MM-DD)
}

interface CaseSchedulingProps {
  data: SchedulingData;
  onChange: (data: SchedulingData) => void;
  compact?: boolean;
}

// ============================================
// HELPER FUNCTIONS
// ============================================

function formatDateForInput(isoString: string | null): string {
  if (!isoString) return "";
  return isoString.split("T")[0];
}

function formatTimeForInput(isoString: string | null): string {
  if (!isoString) return "";
  const date = new Date(isoString);
  return date.toTimeString().slice(0, 5); // HH:MM
}

function combineDateAndTime(date: string, time: string): string | null {
  if (!date) return null;
  if (!time) {
    // Default to 9:00 AM if no time specified
    return `${date}T09:00:00`;
  }
  return `${date}T${time}:00`;
}

function formatDisplayDate(isoString: string | null): string {
  if (!isoString) return "";
  const date = new Date(isoString);
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

function formatDisplayTime(isoString: string | null): string {
  if (!isoString) return "";
  const date = new Date(isoString);
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function formatDisplayDeadline(isoString: string | null): string {
  if (!isoString) return "";
  const date = new Date(isoString + "T00:00:00");
  const now = new Date();
  const diffDays = Math.ceil(
    (date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
  );

  const formatted = date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });

  if (diffDays < 0) {
    return `${formatted} (${Math.abs(diffDays)} days overdue)`;
  } else if (diffDays === 0) {
    return `${formatted} (Today!)`;
  } else if (diffDays === 1) {
    return `${formatted} (Tomorrow)`;
  } else if (diffDays <= 7) {
    return `${formatted} (${diffDays} days)`;
  }
  return formatted;
}

// ============================================
// MAIN COMPONENT
// ============================================

export function CaseScheduling({
  data,
  onChange,
  compact = false,
}: CaseSchedulingProps) {
  const [isExpanded, setIsExpanded] = useState(
    !!(data.scheduledCallAt || data.appealDeadline)
  );

  const handleCallDateChange = (date: string) => {
    const currentTime = formatTimeForInput(data.scheduledCallAt) || "09:00";
    onChange({
      ...data,
      scheduledCallAt: combineDateAndTime(date, currentTime),
    });
  };

  const handleCallTimeChange = (time: string) => {
    const currentDate = formatDateForInput(data.scheduledCallAt);
    if (currentDate) {
      onChange({
        ...data,
        scheduledCallAt: combineDateAndTime(currentDate, time),
      });
    }
  };

  const handleDeadlineChange = (date: string) => {
    onChange({
      ...data,
      appealDeadline: date || null,
    });
  };

  const clearCallSchedule = () => {
    onChange({ ...data, scheduledCallAt: null });
  };

  const clearDeadline = () => {
    onChange({ ...data, appealDeadline: null });
  };

  // Compact display mode (for CaseView header)
  if (compact) {
    return (
      <div className="flex flex-wrap gap-3">
        {data.scheduledCallAt && (
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-sm">
            <IconPhone className="w-4 h-4" />
            <span>
              P2P: {formatDisplayDate(data.scheduledCallAt)} at{" "}
              {formatDisplayTime(data.scheduledCallAt)}
            </span>
          </div>
        )}
        {data.appealDeadline && (
          <div
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm ${
              new Date(data.appealDeadline) < new Date()
                ? "bg-red-50 text-red-700"
                : "bg-amber-50 text-amber-700"
            }`}
          >
            <IconClock className="w-4 h-4" />
            <span>Deadline: {formatDisplayDeadline(data.appealDeadline)}</span>
          </div>
        )}
      </div>
    );
  }

  // Full form mode
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      {/* Header / Toggle */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 flex items-center justify-between bg-slate-50 hover:bg-slate-100 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="p-1.5 bg-white rounded-lg border border-slate-200">
            <IconCalendar className="w-4 h-4 text-slate-600" />
          </div>
          <div className="text-left">
            <h3 className="text-sm font-medium text-slate-700">
              Scheduling & Deadlines
            </h3>
            <p className="text-xs text-slate-500">
              {data.scheduledCallAt || data.appealDeadline
                ? "Reminders set"
                : "Set P2P call time and appeal deadline"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {data.scheduledCallAt && (
            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
              Call scheduled
            </span>
          )}
          {data.appealDeadline && (
            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
              Deadline set
            </span>
          )}
          <IconChevronDown
            className={`w-5 h-5 text-slate-400 transition-transform ${
              isExpanded ? "rotate-180" : ""
            }`}
          />
        </div>
      </button>

      {/* Expandable Content */}
      {isExpanded && (
        <div className="p-4 space-y-4 bg-white">
          {/* P2P Call Schedule */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <IconPhone className="w-4 h-4 text-blue-600" />
                P2P Call Scheduled
              </label>
              {data.scheduledCallAt && (
                <button
                  type="button"
                  onClick={clearCallSchedule}
                  className="text-xs text-slate-400 hover:text-red-500 transition-colors"
                >
                  Clear
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-slate-500 mb-1">Date</label>
                <input
                  type="date"
                  value={formatDateForInput(data.scheduledCallAt)}
                  onChange={(e) => handleCallDateChange(e.target.value)}
                  min={new Date().toISOString().split("T")[0]}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Time</label>
                <input
                  type="time"
                  value={formatTimeForInput(data.scheduledCallAt)}
                  onChange={(e) => handleCallTimeChange(e.target.value)}
                  disabled={!data.scheduledCallAt}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 disabled:bg-slate-50 disabled:text-slate-400"
                />
              </div>
            </div>
            {data.scheduledCallAt && (
              <p className="mt-2 text-xs text-blue-600">
                📱 You'll get a reminder 30 minutes before
              </p>
            )}
          </div>

          {/* Divider */}
          <div className="border-t border-slate-100" />

          {/* Appeal Deadline */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <IconClock className="w-4 h-4 text-amber-600" />
                Appeal Deadline
              </label>
              {data.appealDeadline && (
                <button
                  type="button"
                  onClick={clearDeadline}
                  className="text-xs text-slate-400 hover:text-red-500 transition-colors"
                >
                  Clear
                </button>
              )}
            </div>
            <input
              type="date"
              value={data.appealDeadline || ""}
              onChange={(e) => handleDeadlineChange(e.target.value)}
              min={new Date().toISOString().split("T")[0]}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20"
            />
            {data.appealDeadline && (
              <p className="mt-2 text-xs text-amber-600">
                ⏰ You'll get reminders at 7 days, 3 days, and 1 day before
              </p>
            )}
            <p className="mt-2 text-xs text-slate-400">
              Tip: Most insurers allow 30-60 days from denial date to appeal
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// QUICK SCHEDULE BUTTONS (for CaseView)
// ============================================

interface QuickScheduleProps {
  onScheduleCall: (datetime: string) => void;
  onSetDeadline: (date: string) => void;
}

export function QuickScheduleButtons({
  onScheduleCall,
  onSetDeadline,
}: QuickScheduleProps) {
  const [showCallPicker, setShowCallPicker] = useState(false);
  const [showDeadlinePicker, setShowDeadlinePicker] = useState(false);
  const [callDate, setCallDate] = useState("");
  const [callTime, setCallTime] = useState("09:00");
  const [deadlineDate, setDeadlineDate] = useState("");

  const handleScheduleCall = () => {
    if (callDate) {
      onScheduleCall(`${callDate}T${callTime}:00`);
      setShowCallPicker(false);
      setCallDate("");
    }
  };

  const handleSetDeadline = () => {
    if (deadlineDate) {
      onSetDeadline(deadlineDate);
      setShowDeadlinePicker(false);
      setDeadlineDate("");
    }
  };

  // Quick deadline options
  const deadlineOptions = [
    { label: "30 days", days: 30 },
    { label: "45 days", days: 45 },
    { label: "60 days", days: 60 },
  ];

  const setQuickDeadline = (days: number) => {
    const date = new Date();
    date.setDate(date.getDate() + days);
    onSetDeadline(date.toISOString().split("T")[0]);
    setShowDeadlinePicker(false);
  };

  return (
    <div className="flex flex-wrap gap-2">
      {/* Schedule Call Button */}
      <div className="relative">
        <button
          type="button"
          onClick={() => {
            setShowCallPicker(!showCallPicker);
            setShowDeadlinePicker(false);
          }}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
        >
          <IconPhone className="w-4 h-4" />
          Schedule P2P
        </button>

        {showCallPicker && (
          <div className="absolute top-full left-0 mt-1 p-3 bg-white border border-slate-200 rounded-xl shadow-lg z-10 w-64">
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-slate-500 mb-1">Date</label>
                <input
                  type="date"
                  value={callDate}
                  onChange={(e) => setCallDate(e.target.value)}
                  min={new Date().toISOString().split("T")[0]}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Time</label>
                <input
                  type="time"
                  value={callTime}
                  onChange={(e) => setCallTime(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                />
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setShowCallPicker(false)}
                  className="flex-1 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleScheduleCall}
                  disabled={!callDate}
                  className="flex-1 px-3 py-1.5 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50"
                >
                  Schedule
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Set Deadline Button */}
      <div className="relative">
        <button
          type="button"
          onClick={() => {
            setShowDeadlinePicker(!showDeadlinePicker);
            setShowCallPicker(false);
          }}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors"
        >
          <IconClock className="w-4 h-4" />
          Set Deadline
        </button>

        {showDeadlinePicker && (
          <div className="absolute top-full left-0 mt-1 p-3 bg-white border border-slate-200 rounded-xl shadow-lg z-10 w-64">
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-slate-500 mb-2">
                  Quick options
                </label>
                <div className="flex gap-2">
                  {deadlineOptions.map((opt) => (
                    <button
                      key={opt.days}
                      type="button"
                      onClick={() => setQuickDeadline(opt.days)}
                      className="flex-1 px-2 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg"
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="border-t border-slate-100 pt-3">
                <label className="block text-xs text-slate-500 mb-1">
                  Or pick a date
                </label>
                <input
                  type="date"
                  value={deadlineDate}
                  onChange={(e) => setDeadlineDate(e.target.value)}
                  min={new Date().toISOString().split("T")[0]}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                />
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setShowDeadlinePicker(false)}
                  className="flex-1 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSetDeadline}
                  disabled={!deadlineDate}
                  className="flex-1 px-3 py-1.5 text-sm text-white bg-amber-600 hover:bg-amber-700 rounded-lg disabled:opacity-50"
                >
                  Set
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CaseScheduling;